import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, UserPlus, Pencil, Trash2, ShieldCheck, User as UserIcon, Briefcase, GraduationCap, KeyRound, Copy, Check } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", email: "", mobile: "", role: "user", roles: [], hire_rate: "", disabled: false };

const ALL_ROLES = ["admin", "user", "client", "trainee", "student", "instructor", "mentor"];

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, cls: "bg-lime-100 text-lime-700 border-lime-200" },
  user: { label: "User", icon: UserIcon, cls: "bg-blue-100 text-blue-700 border-blue-200" },
  client: { label: "Client", icon: Briefcase, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  trainee: { label: "Trainee", icon: GraduationCap, cls: "bg-sky-100 text-sky-700 border-sky-200" },
  student: { label: "Student", icon: GraduationCap, cls: "bg-teal-100 text-teal-700 border-teal-200" },
  instructor: { label: "Instructor", icon: UserIcon, cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  mentor: { label: "Mentor", icon: UserIcon, cls: "bg-purple-100 text-purple-700 border-purple-200" },
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user_id or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [confirmGen, setConfirmGen] = useState(null); // user pending password (re)issue
  const [genResult, setGenResult] = useState(null); // { email, password }
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).catch(() => toast.error("Failed to load users")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u.user_id);
    setForm({ first_name: u.first_name || "", last_name: u.last_name || "", email: u.email, mobile: u.mobile || "", role: u.role, roles: u.roles || [], hire_rate: u.hire_rate ?? "", disabled: !!u.disabled });
    setOpen(true);
  };

  const save = async () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, hire_rate: form.hire_rate === "" ? null : +form.hire_rate };
      if (editing) await api.put(`/users/${editing}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "User updated" : "User added");
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    const u = toDelete; setToDelete(null);
    try { await api.delete(`/users/${u.user_id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const doGenerate = async () => {
    const u = confirmGen; setConfirmGen(null);
    setGenerating(true);
    try {
      const res = await api.post(`/users/${u.user_id}/generate-password`);
      setGenResult({ email: res.data.email || u.email, password: res.data.password });
      setCopied(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate password");
    } finally { setGenerating(false); }
  };

  const copyPassword = async () => {
    const text = genResult?.password || "";
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error("clipboard-api-unavailable");
      }
      setCopied(true);
      toast.success("Password copied to clipboard");
    } catch (e) {
      // Fallback for browsers/contexts without the async clipboard API.
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        toast.success("Password copied to clipboard");
      } catch (e2) {
        toast.error("Copy failed — select the password and copy manually");
      }
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Users className="h-6 w-6 text-lime-500" />
          <div>
            <h1 className="text-2xl font-bold font-heading text-slate-900">User Access</h1>
            <p className="text-sm text-slate-500">Only listed, enabled users can sign in with Google.</p>
          </div>
        </div>
        <Button data-testid="add-user-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add User
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-400">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Mobile</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="users-loading">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No users yet.</div>
        ) : (
          users.map((u) => {
            const rm = ROLE_META[u.role] || ROLE_META.user;
            const RoleIcon = rm.icon;
            return (
              <div key={u.user_id} data-testid={`user-row-${u.email}`} className="grid grid-cols-2 md:grid-cols-12 gap-3 px-5 py-4 border-t border-slate-100 items-center">
                <div className="col-span-2 md:col-span-3 flex items-center gap-2 min-w-0">
                  <button onClick={() => navigate(`/users/${u.user_id}`)} data-testid={`user-name-${u.email}`}
                    className="font-medium text-slate-900 truncate hover:text-lime-600 hover:underline underline-offset-2 text-left">
                    {u.name || `${u.first_name} ${u.last_name}`.trim() || "—"}
                  </button>
                  {u.user_id === me?.user_id && <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">You</Badge>}
                  {u.disabled && <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200" data-testid={`user-disabled-${u.email}`}>Disabled</Badge>}
                </div>
                <div className="col-span-1 md:col-span-3 text-sm text-slate-600 truncate">{u.email}</div>
                <div className="col-span-1 md:col-span-2 text-sm text-slate-600">{u.mobile || "—"}</div>
                <div className="col-span-1 md:col-span-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge className={`gap-1 border ${rm.cls}`}><RoleIcon className="h-3 w-3" />{rm.label}</Badge>
                    {(u.roles || []).filter((r) => r !== u.role).map((r) => (
                      <Badge key={r} variant="outline" className="text-[10px] capitalize border-slate-300 text-slate-500" data-testid={`user-extra-role-${u.email}-${r}`}>{r}</Badge>
                    ))}
                  </div>
                  {u.hire_rate != null && <p className="text-[11px] text-slate-400 mt-1" data-testid={`user-hire-rate-${u.email}`}>${u.hire_rate}/hr</p>}
                </div>
                <div className="col-span-1 md:col-span-2 flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" data-testid={`generate-password-${u.email}`} onClick={() => setConfirmGen(u)} className="h-8 w-8 text-slate-500 hover:text-lime-600" title="Generate / re-issue password">
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)} className="h-8 w-8 text-slate-500 hover:text-slate-900">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-user-${u.email}`} disabled={u.user_id === me?.user_id} onClick={() => setToDelete(u)} className="h-8 w-8 text-slate-400 hover:text-red-600 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First name</Label>
                <Input data-testid="user-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Jane" />
              </div>
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input data-testid="user-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Doe" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email (Google sign-in)</Label>
              <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@gmail.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile</Label>
              <Input data-testid="user-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="0400 000 000" />
            </div>
            <div className="space-y-1.5">
              <Label>Primary role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access + manage users</SelectItem>
                  <SelectItem value="user">User — standard access</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="trainee">Trainee</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="instructor">Instructor</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Additional roles</Label>
              <p className="text-xs text-slate-500 -mt-0.5">A person can hold multiple roles (e.g. admin + instructor + mentor).</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {ALL_ROLES.filter((r) => r !== form.role).map((r) => {
                  const on = (form.roles || []).includes(r);
                  return (
                    <button type="button" key={r} data-testid={`user-role-toggle-${r}`}
                      onClick={() => setForm((f) => ({ ...f, roles: on ? f.roles.filter((x) => x !== r) : [...(f.roles || []), r] }))}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all capitalize ${on ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <Checkbox checked={on} className="pointer-events-none h-3.5 w-3.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900" /> {r}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Hire rate ($/hr)</Label>
              <Input data-testid="user-hire-rate" type="number" step="0.01" value={form.hire_rate} onChange={(e) => setForm({ ...form, hire_rate: e.target.value })} placeholder="e.g. 100 (instructor/mentor rate)" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <Label className="text-slate-900">Disabled</Label>
                <p className="text-xs text-slate-500">Lock this user out of the system.</p>
              </div>
              <Switch data-testid="user-disabled-switch" checked={form.disabled} onCheckedChange={(v) => setForm({ ...form, disabled: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-user-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name || toDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>They will immediately lose access and be signed out. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-btn" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!confirmGen} onOpenChange={(v) => !v && setConfirmGen(null)}>
        <AlertDialogContent data-testid="generate-password-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Generate a password for {confirmGen?.name || confirmGen?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              A new password will be created and shown to you once. If they had a password, it will stop working immediately. Use this to issue or re-issue login access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-generate-password-btn" onClick={doGenerate} className="bg-lime-500 hover:bg-lime-400 text-slate-900">Generate password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!genResult} onOpenChange={(v) => { if (!v) { setGenResult(null); setCopied(false); } }}>
        <DialogContent className="max-w-md" data-testid="password-result-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-5 w-5 text-lime-500" /> New password</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-slate-600">Share this with <span className="font-medium text-slate-900">{genResult?.email}</span>. For security it is <span className="font-semibold">shown only once</span> and is stored encrypted — you won't be able to view it again. If it's lost, just generate a new one.</p>
            <div className="flex items-center gap-2">
              <code data-testid="generated-password-value" className="flex-1 select-all font-mono text-lg tracking-wide bg-slate-100 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 break-all">{genResult?.password}</code>
              <Button data-testid="copy-password-btn" onClick={copyPassword} className="h-12 px-3 bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="close-password-result-btn" onClick={() => { setGenResult(null); setCopied(false); }} className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
